import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { toast } from "react-hot-toast";
import { Document, Page, pdfjs } from "react-pdf";
import { Rnd } from "react-rnd";
import {
  HandRaisedIcon,
  CursorArrowRaysIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  XMarkIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  Squares2X2Icon,
  StopIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import cn from "classnames";
import Button from "../common/Button";
import TextArea from "../Settings/TextArea";
import Tooltip from "../common/Tooltip";
import {
  PdfRedactionArea,
  RedactionDocument,
  RedactionFootnote,
  RedactionType,
} from "../../db/schema";
import {
  extractPdfText,
  locateTextMatches,
  exportRedactedPdf,
  identifyDocumentRedactions,
  NoTextError,
  normalizeSelectionRects,
  resolveRedactionPdfUrl,
  resolveTagFontPt,
  BAR_PADDING,
  DEFAULT_TAG_FONT_PT,
  PageTextData,
} from "../../services/redactionPdfService";
import { useSnapGuides } from "../../hooks/useSnapGuides";
import SnapGuideLines from "../common/SnapGuideLines";
import "./RedactionEditor.css";
import { useAuth } from "../../context/AuthContext";
import { useFormDesigner } from "../../context/FormDesignerContext";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { usePdfViewer } from "../../hooks/usePdfViewer";
import { useFieldMovement } from "../../hooks/useFieldMovement";
import { useMarqueeSelection } from "../../hooks/useMarqueeSelection";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import PdfFloatingNav from "../Forms/PdfFloatingNav";
import ResizeHandle from "../common/ResizeHandle";
import { useResizablePanel } from "../../hooks/useResizablePanel";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

// Same palette as the editor's Redactions sidebar tab.
const CATEGORY_OPTIONS: RedactionType["category"][] = [
  "Name",
  "Address",
  "Phone",
  "Email",
  "SSN",
  "DOB",
  "License Plate",
  "Driver's License",
  "Vehicle",
  "Juvenile",
  "Medical",
  "Financial",
  "Other",
];

const CATEGORY_COLORS: Record<RedactionType["category"], string> = {
  Name: "bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-300",
  Address: "bg-purple-100 dark:bg-purple-950/40 text-purple-800 dark:text-purple-400",
  Phone: "bg-green-100 dark:bg-green-950/40 text-green-800 dark:text-green-400",
  Email: "bg-teal-100 dark:bg-teal-950/40 text-teal-800 dark:text-teal-400",
  SSN: "bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-400",
  DOB: "bg-pink-100 dark:bg-pink-950/40 text-pink-800 dark:text-pink-400",
  "License Plate": "bg-yellow-100 dark:bg-yellow-950/40 text-yellow-800 dark:text-amber-400",
  "Driver's License": "bg-orange-100 dark:bg-orange-950/40 text-orange-800 dark:text-orange-400",
  Vehicle: "bg-indigo-100 dark:bg-indigo-950/40 text-indigo-800 dark:text-indigo-400",
  Juvenile: "bg-rose-100 dark:bg-rose-950/40 text-rose-800 dark:text-rose-400",
  Medical: "bg-cyan-100 dark:bg-cyan-950/40 text-cyan-800 dark:text-cyan-400",
  Financial: "bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-400",
  Other: "bg-gray-100 dark:bg-base-200 text-gray-800 dark:text-base-content/80",
};

const newAreaId = () =>
  `redaction-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const TAG_FONT_MIN = 4;
const TAG_FONT_MAX = 24;

type AreaRect = { x: number; y: number; width: number; height: number };

// True when most of the smaller rect is covered by the other — used to skip
// search matches that are already redacted.
const rectsMostlyOverlap = (a: AreaRect, b: AreaRect) => {
  const ix =
    Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x));
  const iy =
    Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));
  const minArea = Math.min(a.width * a.height, b.width * b.height) || 1;
  return (ix * iy) / minArea > 0.6;
};

// Sidebar entries group the rects produced by one identified PII item (a
// phrase can wrap lines or repeat across pages). Manual boxes are their own
// single-area groups.
interface RedactionGroup {
  key: string;
  text?: string;
  category: RedactionType["category"];
  reason?: string;
  tag?: string;
  footnoteId?: string;
  source: "ai" | "manual";
  enabled: boolean;
  areas: PdfRedactionArea[];
}

export interface RedactionEditorProps {
  document: RedactionDocument;
  onSave: (
    redactions: PdfRedactionArea[],
    footnotes: RedactionFootnote[]
  ) => Promise<void>;
  onCancel: () => void;
}

const RedactionEditor: React.FC<RedactionEditorProps> = ({
  document: doc,
  onSave,
  onCancel,
}) => {
  const { reportPreferences } = useAuth();
  const { setIsDesignerOpen } = useFormDesigner();

  useEffect(() => {
    setIsDesignerOpen(true);
    return () => setIsDesignerOpen(false);
  }, [setIsDesignerOpen]);

  const [pdfFileUrl, setPdfFileUrl] = useState<string>("");
  useEffect(() => {
    let cancelled = false;
    resolveRedactionPdfUrl(doc.original_pdf_url).then((u) => {
      if (!cancelled) setPdfFileUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [doc.original_pdf_url]);

  // Older documents carry statute/justification on the areas but predate
  // the managed footnote list — derive one on load (persisted on next save)
  // so unique statute+justification pairs become editable footnotes.
  const { initialAreas, initialFootnotes } = useMemo(() => {
    const rawAreas = doc.redactions || [];
    const existing = doc.footnotes || [];
    const needsDerivation = rawAreas.some(
      (a) =>
        !a.footnoteId && (a.statute?.trim() || a.justification?.trim())
    );
    if (!needsDerivation) {
      return { initialAreas: rawAreas, initialFootnotes: existing };
    }
    const list = [...existing];
    const byKey = new Map<string, RedactionFootnote>(
      existing.map((f) => [
        `${f.statute?.trim() || ""}|${f.justification?.trim() || ""}`,
        f,
      ])
    );
    const mapped = rawAreas.map((a) => {
      if (a.footnoteId || (!a.statute?.trim() && !a.justification?.trim())) {
        return a;
      }
      const key = `${a.statute?.trim() || ""}|${a.justification?.trim() || ""}`;
      let fn = byKey.get(key);
      if (!fn) {
        fn = {
          id: newAreaId(),
          statute: a.statute?.trim() || undefined,
          justification: a.justification?.trim() || undefined,
        };
        byKey.set(key, fn);
        list.push(fn);
      }
      return { ...a, footnoteId: fn.id };
    });
    return { initialAreas: mapped, initialFootnotes: list };
  }, [doc.redactions, doc.footnotes]);

  const {
    state: areas,
    setState: setAreas,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoRedo<PdfRedactionArea[]>(initialAreas);

  const [footnotes, setFootnotes] =
    useState<RedactionFootnote[]>(initialFootnotes);

  const unsavedCurrent = useMemo(
    () => ({ areas, footnotes }),
    [areas, footnotes]
  );
  const unsavedInitial = useMemo(
    () => ({ areas: initialAreas, footnotes: initialFootnotes }),
    [initialAreas, initialFootnotes]
  );

  const { hasChanges, markSaved, handleCancel } = useUnsavedChanges({
    currentState: unsavedCurrent,
    initialState: unsavedInitial,
    onCancel,
  });

  const [selectedTool, setSelectedTool] = useState<string>("select");
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const sidebarPanel = useResizablePanel({
    storageKey: "pnai-redactionSidebarWidth",
    defaultWidth: 320,
    minWidth: 240,
    maxWidth: 640,
    side: "left",
    maxViewportFraction: 0.5,
  });
  const [sidebarTab, setSidebarTab] = useState<"redactions" | "footnotes">(
    "redactions"
  );
  const exportDialogRef = useRef<HTMLDialogElement>(null);
  const reIdentifyDialogRef = useRef<HTMLDialogElement>(null);
  const [instructionsDraft, setInstructionsDraft] = useState("");
  // Editor-only: whether tag labels are drawn on bars in this view. Does not
  // affect the export (that has its own option in the export dialog).
  const [showLabelsInView, setShowLabelsInView] = useState(
    () => localStorage.getItem("redactionDefaults:viewLabels") !== "false"
  );
  // Export dialog options.
  const [exportShowTags, setExportShowTags] = useState(
    () => localStorage.getItem("redactionDefaults:exportTags") !== "false"
  );
  const [exportShowFootnotes, setExportShowFootnotes] = useState(
    () => localStorage.getItem("redactionDefaults:exportFootnotes") !== "false"
  );
  // Label sizing: uniform (one global size for every label, adjustable) or
  // auto (size follows bar height, overridable per redaction).
  const [uniformTagSize, setUniformTagSize] = useState(
    () => localStorage.getItem("redactionDefaults:uniformTagSize") !== "false"
  );
  const [globalTagSize, setGlobalTagSize] = useState(() => {
    const stored = parseInt(
      localStorage.getItem("redactionDefaults:tagFontSize") || "",
      10
    );
    return Number.isFinite(stored) && stored >= TAG_FONT_MIN && stored <= TAG_FONT_MAX
      ? stored
      : DEFAULT_TAG_FONT_PT;
  });
  const tagSizeOptions = useMemo(
    () => ({ uniformTagSize, globalTagSize }),
    [uniformTagSize, globalTagSize]
  );
  const changeGlobalTagSize = useCallback((delta: number) => {
    setGlobalTagSize((prev) => {
      const next = Math.min(TAG_FONT_MAX, Math.max(TAG_FONT_MIN, prev + delta));
      localStorage.setItem("redactionDefaults:tagFontSize", String(next));
      return next;
    });
  }, []);
  const [editingTagKey, setEditingTagKey] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState("");
  // Double-clicking a bar on the canvas opens a tag-edit modal; when the
  // group spans multiple areas the modal doubles as a confirmation that the
  // label changes on all of them.
  const [tagModal, setTagModal] = useState<{
    groupKey: string;
    areaCount: number;
  } | null>(null);
  const [tagModalDraft, setTagModalDraft] = useState("");
  // After redacting new text, a floating bar offers to fold the fresh areas
  // into an existing redaction group (like the Forms bulk-action bar).
  const [pendingMerge, setPendingMerge] = useState<{
    areaIds: string[];
    text: string;
  } | null>(null);

  // Group-to-group merge with a tag conflict prompt: when both groups have
  // different tags, the user picks which label the combined group keeps.
  const [mergeTagConflict, setMergeTagConflict] = useState<{
    sourceKey: string;
    targetKey: string;
    sourceTag: string;
    targetTag: string;
  } | null>(null);

  // Find & Redact panel (Cmd/Ctrl+F) — mirrors the editor's search panel but
  // redacts matches instead of replacing them.
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [currentMatchIdx, setCurrentMatchIdx] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Per-page text (embedded text layer, or OCR for scans). Extracted once
  // per document and shared by AI identification and the Redact Text tool.
  const [pageTextData, setPageTextData] = useState<PageTextData[] | null>(
    null
  );
  const [textProgress, setTextProgress] = useState<string | null>(null);
  const textDataPromise = useRef<Promise<PageTextData[]> | null>(null);

  useEffect(() => {
    // New PDF URL invalidates any cached extraction.
    textDataPromise.current = null;
    setPageTextData(null);
  }, [pdfFileUrl]);

  const ensureTextData = useCallback((): Promise<PageTextData[]> => {
    if (!pdfFileUrl) return Promise.reject(new Error("PDF not loaded"));
    if (!textDataPromise.current) {
      textDataPromise.current = extractPdfText(pdfFileUrl, (p) => {
        setTextProgress(
          p.phase === "ocr"
            ? `Running OCR on page ${p.page} of ${p.total}...`
            : `Reading text (page ${p.page} of ${p.total})...`
        );
      })
        .then((pages) => {
          setPageTextData(pages);
          return pages;
        })
        .catch((err) => {
          textDataPromise.current = null;
          throw err;
        })
        .finally(() => setTextProgress(null));
    }
    return textDataPromise.current;
  }, [pdfFileUrl]);

  const pdf = usePdfViewer({
    storageKey: "redactionEditorZoom",
    selectedTool,
  });
  const { scale } = pdf;

  const currentPageAreas = useMemo(
    () => areas.filter((a) => a.page === pdf.currentPage),
    [areas, pdf.currentPage]
  );

  const marquee = useMarqueeSelection({
    canvasRef: pdf.canvasRef,
    scale: pdf.scale,
    getCanvasCoords: pdf.getCanvasCoords,
    getCurrentPageFields: useCallback(
      () => currentPageAreas.map((a) => ({ id: a.id, rect: a.rect })),
      [currentPageAreas]
    ),
  });
  const { selectedFieldIds, setSelectedFieldIds } = marquee;

  useEffect(() => {
    marquee.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf.currentPage]);

  const getFieldRect = useCallback(
    (areaId: string) => {
      const a = areas.find((x) => x.id === areaId);
      return a ? a.rect : null;
    },
    [areas]
  );

  const onMoveFields = useCallback(
    (
      updates: Array<{
        id: string;
        rect: { x: number; y: number; width: number; height: number };
      }>
    ) => {
      setAreas((prev) =>
        prev.map((a) => {
          const update = updates.find((u) => u.id === a.id);
          return update ? { ...a, rect: update.rect } : a;
        })
      );
    },
    [setAreas]
  );

  // Figma-style alignment snapping with live guide lines (shared with the
  // form designer/filler). The current selection is excluded from targets so
  // a group drag doesn't snap to boxes moving along with it.
  const snap = useSnapGuides({
    getTargetRects: useCallback(
      () => currentPageAreas.map((a) => ({ id: a.id, rect: a.rect })),
      [currentPageAreas]
    ),
    getExcludedIds: useCallback(() => selectedFieldIds, [selectedFieldIds]),
    pageWidth: pdf.pdfDimensions.width,
    pageHeight: pdf.pdfDimensions.height,
    scale,
  });

  const fieldMovement = useFieldMovement({
    scale,
    getFieldRect,
    onMoveFields,
    onResizeField: useCallback(
      (areaId, rect) => {
        setAreas((prev) =>
          prev.map((a) => (a.id === areaId ? { ...a, rect } : a))
        );
      },
      [setAreas]
    ),
    selectedFieldIds,
    snapRect: snap.snapRect,
    onLiveDrag: snap.previewSnap,
    onDragEnd: snap.clearGuides,
    snapResizeRect: snap.snapResizeRect,
    onLiveResize: snap.previewResizeSnap,
  });

  const wasDraggedRef = useRef(false);
  const [isDraggingField, setIsDraggingField] = useState(false);

  // Draw Box tool: in-progress drag rectangle (page-point coords).
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(
    null
  );
  const [drawCurrent, setDrawCurrent] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Clipboard for copy/paste (Cmd+C / Cmd+V), like the form designer.
  const clipboardRef = useRef<PdfRedactionArea[]>([]);

  // --- Persistence ----------------------------------------------------------

  const persist = useCallback(
    async (nextAreas: PdfRedactionArea[], nextFootnotes: RedactionFootnote[]) => {
      await onSave(nextAreas, nextFootnotes);
    },
    [onSave]
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await persist(areas, footnotes);
      markSaved();
      toast.success("Redactions saved");
    } catch (error) {
      console.error("Error saving redactions:", error);
      toast.error("Failed to save redactions");
    } finally {
      setIsSaving(false);
    }
  }, [areas, footnotes, persist, markSaved]);

  // --- AI identification ----------------------------------------------------

  // Runs the same Bedrock `identify-redactions` action the report editor
  // uses, then resolves each returned verbatim string to page rects via the
  // pdf.js text layer. Manual boxes always survive a re-identify.
  const runIdentify = useCallback(
    async (existingAreas: PdfRedactionArea[], instructions?: string) => {
      if (!pdfFileUrl) return;
      setIsIdentifying(true);
      try {
        // Prime the shared text cache so the Redact Text tool reuses it.
        await ensureTextData();
        const result = await identifyDocumentRedactions(pdfFileUrl, {
          docId: doc.id,
          existingAreas,
          existingFootnotes: footnotes,
          reportPreferences,
          redactionInstructions: instructions,
          onProgress: (p) =>
            setTextProgress(
              p.phase === "ocr"
                ? `Running OCR on page ${p.page} of ${p.total}...`
                : `Reading text (page ${p.page} of ${p.total})...`
            ),
        });
        setAreas(result.areas);
        setFootnotes(result.footnotes);
        await persist(result.areas, result.footnotes);
        markSaved();

        const located = result.identifiedCount - result.unmatched;
        toast.success(
          `${located} PII item${located === 1 ? "" : "s"} redacted` +
            (result.unmatched > 0
              ? ` (${result.unmatched} could not be located)`
              : "")
        );
      } catch (error) {
        if (error instanceof NoTextError) {
          toast.error(error.message, { duration: 6000 });
        } else {
          console.error("Error identifying redactions:", error);
          toast.error("Failed to identify redactions");
        }
      } finally {
        setIsIdentifying(false);
        setTextProgress(null);
      }
    },
    [pdfFileUrl, doc.id, reportPreferences, setAreas, persist, markSaved, ensureTextData, footnotes]
  );

  // Fallback: if a document was never identified (e.g. background identify
  // failed or was interrupted), run it once when the editor opens.
  const autoIdentifyRan = useRef(false);
  useEffect(() => {
    if (
      pdfFileUrl &&
      !autoIdentifyRan.current &&
      doc.status === "uploaded" &&
      (doc.redactions || []).length === 0
    ) {
      autoIdentifyRan.current = true;
      runIdentify([], doc.redaction_instructions || undefined);
    }
  }, [
    pdfFileUrl,
    doc.status,
    doc.redactions,
    doc.redaction_instructions,
    runIdentify,
  ]);

  const handleReIdentify = useCallback(() => {
    setInstructionsDraft(doc.redaction_instructions || "");
    reIdentifyDialogRef.current?.showModal();
  }, [doc.redaction_instructions]);

  // Confirming the dialog runs the identify pass with whatever scope the
  // officer typed; the server persists it on the document.
  const handleConfirmReIdentify = useCallback(() => {
    reIdentifyDialogRef.current?.close();
    runIdentify(areas, instructionsDraft.trim());
  }, [runIdentify, areas, instructionsDraft]);

  // --- Export ----------------------------------------------------------------

  const handleExport = useCallback(async () => {
    if (!pdfFileUrl) return;
    exportDialogRef.current?.close();
    setIsExporting(true);
    try {
      if (hasChanges) {
        await persist(areas, footnotes);
        markSaved();
      }
      const blob = await exportRedactedPdf(pdfFileUrl, areas, {
        showTags: exportShowTags,
        showFootnotes: exportShowFootnotes,
        uniformTagSize,
        globalTagSize,
        footnotes,
      });
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = `${doc.name}-redacted.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Redacted PDF exported");
      // Audit trail — fire-and-forget; the export itself already succeeded.
      axios
        .post(`/api/redaction-documents/${doc.id}/log-export`)
        .catch((err) => console.error("Failed to log export audit:", err));
    } catch (error) {
      console.error("Error exporting redacted PDF:", error);
      toast.error("Failed to export redacted PDF");
    } finally {
      setIsExporting(false);
    }
  }, [pdfFileUrl, areas, footnotes, hasChanges, persist, markSaved, exportShowTags, exportShowFootnotes, uniformTagSize, globalTagSize, doc.name, doc.id]);

  // --- Grouping for the sidebar ----------------------------------------------

  const groups = useMemo<RedactionGroup[]>(() => {
    const byKey = new Map<string, RedactionGroup>();
    for (const area of areas) {
      const key = area.groupId || area.id;
      const existing = byKey.get(key);
      if (existing) {
        existing.areas.push(area);
        existing.enabled = existing.enabled || area.enabled;
      } else {
        byKey.set(key, {
          key,
          text: area.text,
          category: area.category,
          reason: area.reason,
          tag: area.tag,
          footnoteId: area.footnoteId,
          source: area.source,
          enabled: area.enabled,
          areas: [area],
        });
      }
    }
    return Array.from(byKey.values());
  }, [areas]);

  const updateGroup = useCallback(
    (groupKey: string, updates: Partial<PdfRedactionArea>) => {
      setAreas((prev) =>
        prev.map((a) =>
          (a.groupId || a.id) === groupKey ? { ...a, ...updates } : a
        )
      );
    },
    [setAreas]
  );

  const deleteGroup = useCallback(
    (groupKey: string) => {
      setAreas((prev) => prev.filter((a) => (a.groupId || a.id) !== groupKey));
      marquee.clearSelection();
      setPendingMerge(null);
    },
    [setAreas, marquee]
  );

  // Fold the just-redacted areas into an existing group: they take the
  // target's groupId and metadata (tag/category/statute) but keep their own
  // matched text.
  const mergePendingIntoGroup = useCallback(
    (targetKey: string) => {
      if (!pendingMerge) return;
      const target = groups.find((g) => g.key === targetKey);
      const meta = target?.areas[0];
      if (!meta) return;
      const ids = new Set(pendingMerge.areaIds);
      setAreas((prev) =>
        prev.map((a) =>
          ids.has(a.id)
            ? {
                ...a,
                groupId: targetKey,
                category: meta.category,
                reason: meta.reason,
                tag: meta.tag,
                statute: meta.statute,
                justification: meta.justification,
                footnoteId: meta.footnoteId,
              }
            : a
        )
      );
      setPendingMerge(null);
      pendingScrollGroupRef.current = targetKey;
    },
    [pendingMerge, groups, setAreas]
  );

  // --- Footnote management ---------------------------------------------------

  const updateFootnote = useCallback(
    (id: string, updates: Partial<RedactionFootnote>) => {
      setFootnotes((prev) =>
        prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
      );
    },
    []
  );

  const addFootnote = useCallback(() => {
    setFootnotes((prev) => [
      ...prev,
      { id: newAreaId(), statute: "", justification: "" },
    ]);
  }, []);

  // Action-bar shortcut: create a fresh footnote, attach it to the group,
  // and reveal the Footnotes panel with the new statute input focused.
  const pendingFocusFootnoteRef = useRef<string | null>(null);
  const addFootnoteForGroup = useCallback(
    (groupKey: string) => {
      const id = newAreaId();
      setFootnotes((prev) => [
        ...prev,
        { id, statute: "", justification: "" },
      ]);
      updateGroup(groupKey, { footnoteId: id });
      pendingFocusFootnoteRef.current = id;
      setSidebarTab("footnotes");
      setSidebarCollapsed(false);
    },
    [updateGroup]
  );

  const deleteFootnote = useCallback(
    (id: string) => {
      setFootnotes((prev) => prev.filter((f) => f.id !== id));
      // Unlink any redactions referencing it.
      setAreas((prev) =>
        prev.map((a) =>
          a.footnoteId === id ? { ...a, footnoteId: undefined } : a
        )
      );
    },
    [setAreas]
  );

  // Usage counts so the panel can show what each footnote is attached to.
  const footnoteUsage = useMemo(() => {
    const counts = new Map<string, number>();
    for (const g of groups) {
      if (g.footnoteId) {
        counts.set(g.footnoteId, (counts.get(g.footnoteId) || 0) + 1);
      }
    }
    return counts;
  }, [groups]);

  // Short display line for a footnote, mirroring the export format.
  const footnoteLabel = (f: RedactionFootnote) =>
    `${f.statute?.trim() ? `(${f.statute.trim()}) ` : ""}${
      f.justification?.trim() || ""
    }`.trim() || "(empty)";

  // Display numbers match the export exactly: 1-based position among
  // footnotes that have content (empty drafts get no number).
  const footnoteNumbers = useMemo(() => {
    const map = new Map<string, number>();
    let n = 0;
    for (const f of footnotes) {
      if (f.statute?.trim() || f.justification?.trim()) {
        n += 1;
        map.set(f.id, n);
      }
    }
    return map;
  }, [footnotes]);

  // Merge one whole group into another. When both have (different) tags the
  // caller is asked to resolve the conflict first; the chosen tag is applied
  // to every area of the combined group.
  const mergeGroups = useCallback(
    (sourceKey: string, targetKey: string, tagOverride?: string) => {
      const source = groups.find((g) => g.key === sourceKey);
      const target = groups.find((g) => g.key === targetKey);
      const meta = target?.areas[0];
      if (!source || !target || !meta) return;

      const sourceTag = source.tag?.trim() || "";
      const targetTag = target.tag?.trim() || "";
      if (tagOverride === undefined && sourceTag && targetTag && sourceTag !== targetTag) {
        setMergeTagConflict({ sourceKey, targetKey, sourceTag, targetTag });
        return;
      }
      const finalTag =
        tagOverride !== undefined
          ? tagOverride || undefined
          : targetTag || sourceTag || undefined;

      setAreas((prev) =>
        prev.map((a) => {
          const key = a.groupId || a.id;
          if (key === sourceKey) {
            return {
              ...a,
              groupId: targetKey,
              category: meta.category,
              reason: meta.reason,
              statute: meta.statute,
              justification: meta.justification,
              footnoteId: meta.footnoteId,
              tag: finalTag,
            };
          }
          if (key === targetKey) {
            return { ...a, tag: finalTag };
          }
          return a;
        })
      );
      setMergeTagConflict(null);
      pendingScrollGroupRef.current = targetKey;
      toast.success("Redactions merged");
    },
    [groups, setAreas]
  );

  // Scroll the sidebar entry for a selected redaction into view (e.g. when a
  // bar is clicked on the PDF). block: "nearest" keeps this a no-op when the
  // entry is already visible, so sidebar-initiated selections don't jump.
  const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  useEffect(() => {
    if (selectedFieldIds.size === 0) return;
    const selectedArea = areas.find((a) => selectedFieldIds.has(a.id));
    if (!selectedArea) return;
    const groupKey = selectedArea.groupId || selectedArea.id;
    groupRefs.current
      .get(groupKey)
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFieldIds]);

  // Set before adding areas whose sidebar entry doesn't exist yet (newly
  // redacted text); the effect scrolls to it once the entry has rendered.
  const pendingScrollGroupRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingScrollGroupRef.current) return;
    const el = groupRefs.current.get(pendingScrollGroupRef.current);
    if (el) {
      pendingScrollGroupRef.current = null;
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [areas]);

  const jumpToGroup = useCallback(
    (group: RedactionGroup) => {
      const first = group.areas[0];
      if (!first) return;
      if (first.page !== pdf.currentPage) {
        pdf.goToPage(first.page);
      }
      setSelectedFieldIds(new Set(group.areas.map((a) => a.id)));
    },
    [pdf, setSelectedFieldIds]
  );

  // --- Find & Redact ---------------------------------------------------------

  const searchMatches = useMemo(() => {
    if (!showSearch || !searchTerm.trim() || !pageTextData) return [];
    const matches: { page: number; rects: AreaRect[] }[] = [];
    for (const pageData of pageTextData) {
      for (const m of locateTextMatches(pageData, searchTerm, {
        caseSensitive,
      })) {
        matches.push({ page: pageData.page, rects: m.rects });
      }
    }
    return matches;
  }, [showSearch, searchTerm, caseSensitive, pageTextData]);

  const clampedMatchIdx = Math.min(
    currentMatchIdx,
    Math.max(0, searchMatches.length - 1)
  );

  useEffect(() => {
    setCurrentMatchIdx(0);
  }, [searchTerm, caseSensitive]);

  // Follow the current match: switch pages and scroll it into view.
  useEffect(() => {
    if (!showSearch || searchMatches.length === 0) return;
    const match = searchMatches[clampedMatchIdx];
    if (!match) return;
    if (match.page !== pdf.currentPage) {
      pdf.goToPage(match.page);
    }
    const container = pdf.containerRef.current;
    const rect = match.rects[0];
    if (container && rect) {
      container.scrollTo({
        top: Math.max(0, rect.y * scale - container.clientHeight / 2 + 32),
        behavior: "smooth",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSearch, clampedMatchIdx, searchMatches]);

  const openSearch = useCallback(() => {
    setShowSearch(true);
    ensureTextData().catch((err) => {
      console.error("Error preparing text for search:", err);
      toast.error("Failed to read document text");
    });
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [ensureTextData]);

  const closeSearch = useCallback(() => {
    setShowSearch(false);
    setSearchTerm("");
    setCurrentMatchIdx(0);
  }, []);

  const gotoMatch = useCallback(
    (delta: number) => {
      if (searchMatches.length === 0) return;
      setCurrentMatchIdx(
        (prev) =>
          (Math.min(prev, searchMatches.length - 1) +
            delta +
            searchMatches.length) %
          searchMatches.length
      );
    },
    [searchMatches.length]
  );

  // Redact the current match or every match, skipping ones already covered
  // by an existing bar. All new areas share one group; the merge bar then
  // offers to fold them into an existing redaction.
  const redactSearchMatches = useCallback(
    (all: boolean) => {
      const term = searchTerm.trim();
      if (!term || searchMatches.length === 0) return;
      const targets = all
        ? searchMatches
        : [searchMatches[clampedMatchIdx]].filter(Boolean);

      const groupId = newAreaId();
      const newAreas: PdfRedactionArea[] = [];
      // Count whole matches actually redacted (not rects) so the toast
      // reflects what happened — already-covered matches are skipped.
      let redactedMatches = 0;
      for (const m of targets) {
        let addedAny = false;
        for (const rect of m.rects) {
          const covered = areas.some(
            (a) => a.page === m.page && rectsMostlyOverlap(a.rect, rect)
          );
          if (covered) continue;
          addedAny = true;
          newAreas.push({
            id: newAreaId(),
            groupId,
            page: m.page,
            rect,
            text: term,
            category: "Other",
            source: "manual",
            enabled: true,
          });
        }
        if (addedAny) redactedMatches++;
      }

      if (newAreas.length === 0) {
        toast(
          targets.length === 1
            ? "This match is already redacted"
            : "All matches are already redacted"
        );
        return;
      }
      pendingScrollGroupRef.current = groupId;
      setAreas((prev) => [...prev, ...newAreas]);
      setPendingMerge({ areaIds: newAreas.map((a) => a.id), text: term });
      const skipped = targets.length - redactedMatches;
      toast.success(
        all
          ? `Redacted ${redactedMatches} instance${
              redactedMatches === 1 ? "" : "s"
            } of "${term}"` +
              (skipped > 0 ? ` (${skipped} already redacted)` : "")
          : "Match redacted"
      );
    },
    [searchTerm, searchMatches, clampedMatchIdx, areas, setAreas]
  );

  // --- Keyboard shortcuts -----------------------------------------------------

  useEffect(() => {
    const isInputFocused = () => {
      const el = window.document.activeElement;
      return (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement
      );
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        e.preventDefault();
        openSearch();
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        if (isInputFocused()) return;
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        return;
      }

      // Ctrl/Cmd+C copy selected redactions
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === "c" &&
        selectedFieldIds.size > 0 &&
        !isInputFocused()
      ) {
        e.preventDefault();
        clipboardRef.current = areas.filter((a) => selectedFieldIds.has(a.id));
        return;
      }

      // Ctrl/Cmd+V paste copied redactions onto the current page. Source
      // groups are preserved among the copies but as NEW groups, so pasted
      // bars are independent entries; copies are always "manual" so a
      // re-identify never wipes them.
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === "v" &&
        clipboardRef.current.length > 0 &&
        !isInputFocused()
      ) {
        e.preventDefault();
        const offset = 15;
        const groupIdMap = new Map<string, string>();
        const newAreas = clipboardRef.current.map((a) => {
          const oldKey = a.groupId || a.id;
          if (!groupIdMap.has(oldKey)) groupIdMap.set(oldKey, newAreaId());
          return {
            ...a,
            id: newAreaId(),
            groupId: groupIdMap.get(oldKey),
            source: "manual" as const,
            rect: { ...a.rect, x: a.rect.x + offset, y: a.rect.y + offset },
            page: pdf.currentPage,
          };
        });
        setAreas((prev) => [...prev, ...newAreas]);
        setSelectedFieldIds(new Set(newAreas.map((a) => a.id)));
        // Shift clipboard so repeated pastes cascade
        clipboardRef.current = newAreas;
        return;
      }

      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === "a" &&
        selectedTool === "select" &&
        !isInputFocused()
      ) {
        e.preventDefault();
        marquee.selectAll();
        return;
      }

      if (e.key === "Escape") {
        if (tagModal) {
          setTagModal(null);
          return;
        }
        if (mergeTagConflict) {
          setMergeTagConflict(null);
          return;
        }
        if (showSearch) {
          closeSearch();
          return;
        }
        marquee.clearSelection();
        setSelectedTool("select");
        setPendingMerge(null);
        setDrawStart(null);
        setDrawCurrent(null);
        window.getSelection()?.removeAllRanges();
        return;
      }

      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedFieldIds.size > 0 &&
        !isInputFocused()
      ) {
        e.preventDefault();
        setAreas((prev) => prev.filter((a) => !selectedFieldIds.has(a.id)));
        marquee.clearSelection();
        setPendingMerge(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedFieldIds, selectedTool, setAreas, undo, redo, marquee, showSearch, openSearch, closeSearch, mergeTagConflict, tagModal, areas, pdf.currentPage, setSelectedFieldIds]);

  // --- Canvas mouse handling ---------------------------------------------------

  const handleContainerMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (pdf.handleContainerMouseDown(e)) return;
      if (selectedTool === "select") {
        marquee.startMarquee(e);
      }
    },
    [selectedTool, pdf, marquee]
  );

  // Temporary drag of an existing bar while the Redact Text tool is active:
  // mousedown on a bar acts like the Select tool for the duration of the
  // hold (move only), then the tool is back to text selection on release.
  // The offset is visual (CSS transform, screen px) during the drag and
  // committed to the rect once on mouseup, so it's a single undo entry.
  const [tempDrag, setTempDrag] = useState<{
    areaId: string;
    dx: number;
    dy: number;
  } | null>(null);

  const handleBarMouseDown = useCallback(
    (e: React.MouseEvent, areaId: string) => {
      if (selectedTool !== "redact" || e.button !== 0) return;
      // Keep this from starting a text selection or a container pan.
      e.preventDefault();
      e.stopPropagation();

      const origRect = areas.find((a) => a.id === areaId)?.rect;
      if (!origRect) return;

      const startX = e.clientX;
      const startY = e.clientY;

      const proposedAt = (ev: MouseEvent) => ({
        ...origRect,
        x: origRect.x + (ev.clientX - startX) / scale,
        y: origRect.y + (ev.clientY - startY) / scale,
      });

      const onMove = (ev: MouseEvent) => {
        const rect = snap.liveSnap(areaId, proposedAt(ev));
        setTempDrag({
          areaId,
          dx: (rect.x - origRect.x) * scale,
          dy: (rect.y - origRect.y) * scale,
        });
      };
      const onUp = (ev: MouseEvent) => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        setTempDrag(null);
        const rawDx = (ev.clientX - startX) / scale;
        const rawDy = (ev.clientY - startY) / scale;
        if (Math.abs(rawDx) > 0.5 || Math.abs(rawDy) > 0.5) {
          const rect = snap.snapRect(areaId, proposedAt(ev));
          setAreas((prev) =>
            prev.map((a) => (a.id === areaId ? { ...a, rect } : a))
          );
        } else {
          // Plain click (no drag): select the redaction just like the Select
          // tool would — highlights it and scrolls the sidebar entry into
          // view via the selection effect.
          marquee.handleFieldClick(areaId, ev.shiftKey);
        }
        snap.clearGuides();
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [selectedTool, scale, setAreas, areas, snap, marquee]
  );

  // Turn the current browser text selection (over the invisible text layer)
  // into redaction areas — one bar per visual line, all sharing a group so
  // the sidebar shows a single entry with the selected text.
  const commitTextSelection = useCallback(() => {
    const sel = window.getSelection();
    const canvasEl = pdf.canvasRef.current;
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !canvasEl) return;

    const layer = canvasEl.querySelector(".redaction-text-layer");
    if (
      !layer ||
      !layer.contains(sel.anchorNode) ||
      !layer.contains(sel.focusNode)
    ) {
      return;
    }

    const text = sel.toString().replace(/\s+/g, " ").trim();
    const canvasRect = canvasEl.getBoundingClientRect();
    const raw: { x: number; y: number; width: number; height: number }[] = [];
    for (let i = 0; i < sel.rangeCount; i++) {
      for (const r of Array.from(sel.getRangeAt(i).getClientRects())) {
        raw.push({
          x: (r.left - canvasRect.left) / scale,
          y: (r.top - canvasRect.top) / scale,
          width: r.width / scale,
          height: r.height / scale,
        });
      }
    }
    sel.removeAllRanges();

    const rects = normalizeSelectionRects(raw);
    if (rects.length === 0 || !text) return;

    const groupId = newAreaId();
    const newAreas: PdfRedactionArea[] = rects.map((rect) => ({
      id: newAreaId(),
      groupId,
      page: pdf.currentPage,
      rect,
      text,
      category: "Other",
      source: "manual",
      enabled: true,
    }));
    pendingScrollGroupRef.current = groupId;
    setAreas((prev) => [...prev, ...newAreas]);
    setPendingMerge({ areaIds: newAreas.map((a) => a.id), text });
  }, [pdf, scale, setAreas]);

  const handleContainerMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      pdf.handleContainerMouseMove(e);
    },
    [pdf]
  );

  const handleContainerMouseUp = useCallback(() => {
    pdf.handleContainerMouseEnd();
    if (selectedTool === "redact") {
      commitTextSelection();
    }
  }, [pdf, selectedTool, commitTextSelection]);

  // --- Draw Box tool ---------------------------------------------------------
  // Click-and-drag anywhere on the page to place a freeform redaction box —
  // no underlying text required, so it covers photos, signatures, and scanned
  // regions the text tools can't reach. The result is an ordinary manual
  // redaction: it can be labeled, footnoted, moved, resized, and exports as a
  // true rasterized redaction like every other bar.
  const clampToPage = useCallback(
    (p: { x: number; y: number }) => ({
      x: Math.min(Math.max(p.x, 0), pdf.pdfDimensions.width),
      y: Math.min(Math.max(p.y, 0), pdf.pdfDimensions.height),
    }),
    [pdf.pdfDimensions]
  );

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (selectedTool !== "draw" || e.button !== 0 || pdf.isSpaceHeld) return;
      // A selected bar is wrapped in react-rnd — let it drag/resize instead
      // of starting a new box underneath it.
      if ((e.target as HTMLElement).closest(".react-draggable")) return;
      e.preventDefault();
      const coords = clampToPage(pdf.getCanvasCoords(e));
      setDrawStart(coords);
      setDrawCurrent(coords);
    },
    [selectedTool, pdf, clampToPage]
  );

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (drawStart) setDrawCurrent(clampToPage(pdf.getCanvasCoords(e)));
    },
    [drawStart, pdf, clampToPage]
  );

  const handleCanvasMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!drawStart) return;
      const end = clampToPage(pdf.getCanvasCoords(e));
      setDrawStart(null);
      setDrawCurrent(null);

      const rect = {
        x: Math.min(drawStart.x, end.x),
        y: Math.min(drawStart.y, end.y),
        width: Math.abs(end.x - drawStart.x),
        height: Math.abs(end.y - drawStart.y),
      };
      // Ignore plain clicks and accidental slivers.
      if (rect.width < 4 || rect.height < 4) return;

      const area: PdfRedactionArea = {
        id: newAreaId(),
        page: pdf.currentPage,
        rect,
        text: "",
        category: "Other",
        source: "manual",
        enabled: true,
      };
      pendingScrollGroupRef.current = area.id;
      setAreas((prev) => [...prev, area]);
      // Select it so the floating bar is right there for labeling; the tool
      // stays active for drawing the next box.
      setSelectedFieldIds(new Set([area.id]));
    },
    [drawStart, pdf, clampToPage, setAreas, setSelectedFieldIds]
  );

  const drawPreview =
    selectedTool === "draw" && drawStart && drawCurrent
      ? {
          left: Math.min(drawStart.x, drawCurrent.x) * scale,
          top: Math.min(drawStart.y, drawCurrent.y) * scale,
          width: Math.abs(drawCurrent.x - drawStart.x) * scale,
          height: Math.abs(drawCurrent.y - drawStart.y) * scale,
        }
      : null;

  // Entering Redact Text mode needs the text layer (may trigger OCR for
  // scanned pages) and shouldn't keep a stale box selection around.
  useEffect(() => {
    if (selectedTool !== "redact") {
      setPendingMerge(null);
    }
    if (selectedTool === "redact") {
      marquee.clearSelection();
      ensureTextData().catch((err) => {
        console.error("Error preparing text layer:", err);
        toast.error("Failed to prepare the text layer for this PDF");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTool, ensureTextData]);

  // Invisible selectable spans for the current page. scaleX compensates for
  // the difference between the browser font's natural width and the true
  // width of the text on the page, so selection highlights line up.
  const measureCtx = useMemo(
    () => document.createElement("canvas").getContext("2d"),
    []
  );
  const currentPageText = useMemo(
    () => pageTextData?.find((p) => p.page === pdf.currentPage) || null,
    [pageTextData, pdf.currentPage]
  );
  const selectionSpans = useMemo(() => {
    if (!currentPageText || !measureCtx) return [];
    return currentPageText.items.map((item) => {
      const fontSize = Math.max(item.height * 0.85, 4) * scale;
      measureCtx.font = `${fontSize}px sans-serif`;
      const measured = measureCtx.measureText(item.str).width || 1;
      return {
        ...item,
        fontSize,
        scaleX: (item.width * scale) / measured,
      };
    });
  }, [currentPageText, measureCtx, scale]);

  // --- Derived -------------------------------------------------------------

  // Merge-bar targets: every group except the one the pending areas are in.
  const pendingMergeKey = useMemo(() => {
    if (!pendingMerge) return null;
    const first = areas.find((a) => a.id === pendingMerge.areaIds[0]);
    return first ? first.groupId || first.id : null;
  }, [pendingMerge, areas]);
  const mergeTargets = useMemo(
    () =>
      pendingMerge ? groups.filter((g) => g.key !== pendingMergeKey) : [],
    [pendingMerge, groups, pendingMergeKey]
  );

  // Group keys of the current selection — sibling areas of a selected
  // redaction get a secondary highlight so the group reads as one unit.
  const selectedGroupKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const a of areas) {
      if (selectedFieldIds.has(a.id)) keys.add(a.groupId || a.id);
    }
    return keys;
  }, [areas, selectedFieldIds]);

  // When the selection is exactly one group, the floating bar exposes the
  // group's category/footnote/label-size controls and offers to merge it
  // into any other group.
  const selectedGroupKey =
    selectedGroupKeys.size === 1 ? Array.from(selectedGroupKeys)[0] : null;
  const selectedGroup = useMemo(
    () =>
      selectedGroupKey
        ? groups.find((g) => g.key === selectedGroupKey) || null
        : null,
    [groups, selectedGroupKey]
  );
  const groupMergeTargets = useMemo(
    () =>
      selectedGroupKey
        ? groups.filter((g) => g.key !== selectedGroupKey)
        : [],
    [groups, selectedGroupKey]
  );
  // Total areas across every group the selection touches — the floating bar
  // shows "selected/total" when the selection is part of larger group(s).
  const selectedGroupsAreaTotal = useMemo(
    () => areas.filter((a) => selectedGroupKeys.has(a.groupId || a.id)).length,
    [areas, selectedGroupKeys]
  );

  const enabledCount = areas.filter((a) => a.enabled).length;
  // Export-dialog card enablement: how many active groups carry a tag, and
  // whether any contentful footnotes exist.
  const taggedGroupCount = useMemo(
    () => groups.filter((g) => g.enabled && g.tag?.trim()).length,
    [groups]
  );
  const hasFootnotes = footnoteNumbers.size > 0;

  const startTagEdit = (group: RedactionGroup) => {
    setEditingTagKey(group.key);
    setTagDraft(group.tag || "");
  };

  const saveTagEdit = (groupKey: string) => {
    updateGroup(groupKey, { tag: tagDraft.trim() || undefined });
    setEditingTagKey(null);
  };

  const openTagModal = (area: PdfRedactionArea) => {
    const key = area.groupId || area.id;
    const areaCount = areas.filter((a) => (a.groupId || a.id) === key).length;
    setTagModalDraft(area.tag || "");
    setTagModal({ groupKey: key, areaCount });
  };

  const saveTagModal = () => {
    if (!tagModal) return;
    updateGroup(tagModal.groupKey, { tag: tagModalDraft.trim() || undefined });
    setTagModal(null);
  };

  return (
    <div className="flex-1 min-w-0 h-screen flex flex-col bg-base-200">
      {/* Header — mirrors the investigation detail header */}
      <div className="bg-base-100 border-b border-base-300 px-6 py-4">
        <div className="flex items-start gap-3 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            className="mt-0.5"
            onClick={handleCancel}
            title={hasChanges ? "Discard changes and go back" : "All redactions"}
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            {hasChanges ? "Cancel" : "Redactions"}
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-base-content truncate">
              {doc.name}
            </h1>
            <p className="text-xs text-base-content/60">
              {enabledCount} active redaction{enabledCount === 1 ? "" : "s"}
              {hasChanges && (
                <span className="text-amber-700 dark:text-amber-400">
                  {" "}
                  · unsaved changes
                </span>
              )}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="px-2"
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
          >
            <ArrowUturnLeftIcon className="size-4" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="px-2"
            onClick={redo}
            disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z)"
          >
            <ArrowUturnRightIcon className="size-4" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            title="Save redactions"
          >
            <CheckIcon className="h-4 w-4 mr-1" />
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => exportDialogRef.current?.showModal()}
            disabled={isExporting || isIdentifying}
            title="Export the redacted PDF"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
            {isExporting ? "Exporting..." : "Export"}
          </Button>
        </div>
      </div>

      {/* Find & Redact panel (Cmd/Ctrl+F) — mirrors the editor's search
          panel, minus find-and-replace */}
      <div
        className={`overflow-hidden border-t-2 bg-base-100 transition-all duration-200 ease-in-out ${
          showSearch ? "max-h-40 opacity-100 p-2" : "max-h-0 opacity-0 p-0"
        }`}
      >
        {showSearch && (
          <div className="flex items-center space-x-1">
            <div className="flex-1 relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    gotoMatch(e.shiftKey ? -1 : 1);
                  }
                }}
                placeholder="Find text to redact"
                className="input input-bordered max-h-7 w-full focus:outline-none focus:border-blue-500 border-grey-500 transition-colors bg-base-100 text-sm px-2 pr-14"
                autoFocus
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-base-content/40 pointer-events-none select-none">
                {searchMatches.length > 0
                  ? `${clampedMatchIdx + 1}/${searchMatches.length}`
                  : "0/0"}
              </span>
            </div>
            <Tooltip overlay="Previous (Shift+Enter)">
              <button
                onClick={() => gotoMatch(-1)}
                disabled={searchMatches.length === 0}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-base-200 rounded disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronUpIcon className="size-4" />
              </button>
            </Tooltip>
            <Tooltip overlay="Next (Enter)">
              <button
                onClick={() => gotoMatch(1)}
                disabled={searchMatches.length === 0}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-base-200 rounded disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronDownIcon className="size-4" />
              </button>
            </Tooltip>
            <Tooltip overlay="Match Case">
              <button
                onClick={() => setCaseSensitive((v) => !v)}
                className={cn(
                  "p-1.5 rounded transition-colors font-semibold text-xs",
                  caseSensitive
                    ? "bg-blue-100 dark:bg-blue-900/60 hover:bg-blue-200 dark:hover:bg-blue-900/80 text-blue-700 dark:text-blue-300"
                    : "hover:bg-gray-100 dark:hover:bg-base-200 text-gray-600 dark:text-base-content/70"
                )}
              >
                Aa
              </button>
            </Tooltip>
            <Tooltip overlay="Redact current match" placement="bottomRight">
              <Button
                variant="secondary"
                size="xs"
                onClick={() => redactSearchMatches(false)}
                disabled={searchMatches.length === 0}
              >
                Redact
              </Button>
            </Tooltip>
            <Tooltip overlay="Redact every match" placement="bottomRight">
              <Button
                variant="primary"
                size="xs"
                onClick={() => redactSearchMatches(true)}
                disabled={searchMatches.length === 0}
              >
                Redact All
              </Button>
            </Tooltip>
            <Tooltip overlay="Close (Esc)" placement="bottomRight">
              <button
                onClick={closeSearch}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-base-200 rounded"
              >
                <XMarkIcon className="size-4" />
              </button>
            </Tooltip>
          </div>
        )}
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Toolbar */}
        <div className="w-44 flex-shrink-0 bg-base-100 border-r border-base-300 flex flex-col py-3 gap-0.5 px-2">
          <Tooltip overlay="Select redactions" placement="right">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setSelectedTool("select")}
              className={cn(
                "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                selectedTool === "select"
                  ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "hover:bg-base-300 text-gray-700 dark:text-base-content/80"
              )}
            >
              <div className="flex items-center pl-1">
                <CursorArrowRaysIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Select</span>
              </div>
            </button>
          </Tooltip>
          <Tooltip overlay="Click and drag to pan" placement="right">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setSelectedTool("pan");
                marquee.clearSelection();
              }}
              className={cn(
                "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                selectedTool === "pan"
                  ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "hover:bg-base-300 text-gray-700 dark:text-base-content/80"
              )}
            >
              <div className="flex items-center pl-1">
                <HandRaisedIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Pan</span>
              </div>
            </button>
          </Tooltip>

          <div className="my-2 border-t border-base-300" />

          <Tooltip
            overlay="Select text on the PDF to redact it (scanned pages are OCR'd)"
            placement="right"
          >
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setSelectedTool("redact");
                marquee.clearSelection();
              }}
              className={cn(
                "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                selectedTool === "redact"
                  ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "hover:bg-base-300 text-gray-700 dark:text-base-content/80"
              )}
            >
              <div className="flex items-center pl-1">
                <EyeSlashIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Redact Text</span>
              </div>
            </button>
          </Tooltip>

          <Tooltip
            overlay="Drag to draw a redaction box over anything — photos, signatures, or regions without text. Double-click a box to label it"
            placement="right"
          >
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setSelectedTool("draw");
                marquee.clearSelection();
              }}
              className={cn(
                "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                selectedTool === "draw"
                  ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "hover:bg-base-300 text-gray-700 dark:text-base-content/80"
              )}
            >
              <div className="flex items-center pl-1">
                <StopIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Redact Box</span>
              </div>
            </button>
          </Tooltip>

          <Tooltip
            overlay="Find text and redact all instances (Cmd/Ctrl+F)"
            placement="right"
          >
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => (showSearch ? closeSearch() : openSearch())}
              className={cn(
                "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                showSearch
                  ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "hover:bg-base-300 text-gray-700 dark:text-base-content/80"
              )}
            >
              <div className="flex items-center pl-1">
                <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Find & Redact</span>
              </div>
            </button>
          </Tooltip>

          <Tooltip
            overlay="Re-run AI identification (manual redactions are kept)"
            placement="right"
          >
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleReIdentify}
              disabled={isIdentifying || !pdfFileUrl}
              className="btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px] hover:bg-base-300 text-gray-700 dark:text-base-content/80 disabled:opacity-50"
            >
              <div className="flex items-center pl-1">
                <ArrowPathIcon
                  className={cn("h-5 w-5 mr-2", isIdentifying && "animate-spin")}
                />
                <span className="text-sm">
                  {isIdentifying ? "Identifying..." : "Re-identify"}
                </span>
              </div>
            </button>
          </Tooltip>

          <div className="mt-auto px-2 pt-3 border-t border-base-300 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-3.5 h-3.5 text-blue-600 dark:text-blue-300 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                checked={snap.snapEnabled}
                onChange={(e) => snap.setSnapEnabled(e.target.checked)}
              />
              <span className="text-xs text-base-content/60">Snap to guides</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-3.5 h-3.5 text-blue-600 dark:text-blue-300 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                checked={showLabelsInView}
                onChange={(e) => {
                  setShowLabelsInView(e.target.checked);
                  localStorage.setItem(
                    "redactionDefaults:viewLabels",
                    String(e.target.checked)
                  );
                }}
              />
              <span className="text-xs text-base-content/60">View labels on bars</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-3.5 h-3.5 text-blue-600 dark:text-blue-300 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                checked={uniformTagSize}
                onChange={(e) => {
                  setUniformTagSize(e.target.checked);
                  localStorage.setItem(
                    "redactionDefaults:uniformTagSize",
                    String(e.target.checked)
                  );
                }}
              />
              <span className="text-xs text-base-content/60">Standard text size</span>
            </label>
            {uniformTagSize ? (
              <div className="flex items-center gap-1.5 pl-5">
                <button
                  onClick={() => changeGlobalTagSize(-1)}
                  className="w-5 h-5 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-xs font-medium"
                >
                  -
                </button>
                <span className="text-xs text-gray-600 dark:text-base-content/70 w-6 text-center">
                  {globalTagSize}
                </span>
                <button
                  onClick={() => changeGlobalTagSize(1)}
                  className="w-5 h-5 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-xs font-medium"
                >
                  +
                </button>
                <span className="text-[10px] text-base-content/40">pt</span>
              </div>
            ) : (
              <p className="text-[10px] text-base-content/40 pl-5">
                Text auto-sizes to each bar; adjust per redaction in the
                sidebar
              </p>
            )}
            <p className="text-xs text-base-content/40">
              Hold{" "}
              <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-base-200 border border-gray-300 dark:border-base-300 rounded text-[10px] font-mono">
                Space
              </kbd>{" "}
              to pan
            </p>
          </div>
        </div>

        {/* PDF Canvas */}
        <div className="flex-1 min-w-0 relative">
          {/* Text extraction / OCR / identify progress banner */}
          {(isIdentifying || textProgress) && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30">
              <div className="bg-base-100 shadow-lg border border-base-300 rounded-lg px-4 py-2 flex items-center gap-2">
                <span className="loading loading-spinner loading-xs text-primary" />
                <span className="text-sm text-gray-700 dark:text-base-content/80">
                  {textProgress || "Identifying PII to redact..."}
                </span>
              </div>
            </div>
          )}

          {/* Tag conflict prompt — both merged groups have labels */}
          {mergeTagConflict && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30">
              <div className="bg-base-100 shadow-lg border border-base-300 rounded-lg px-4 py-2 flex items-center gap-3">
                <span className="text-sm text-gray-700 dark:text-base-content/80">
                  Both have tags — keep which label?
                </span>
                <div className="w-px h-5 bg-base-300" />
                <button
                  onClick={() =>
                    mergeGroups(
                      mergeTagConflict.sourceKey,
                      mergeTagConflict.targetKey,
                      mergeTagConflict.sourceTag
                    )
                  }
                  className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-xs font-medium hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
                >
                  [{mergeTagConflict.sourceTag}]
                </button>
                <button
                  onClick={() =>
                    mergeGroups(
                      mergeTagConflict.sourceKey,
                      mergeTagConflict.targetKey,
                      mergeTagConflict.targetTag
                    )
                  }
                  className="px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 text-xs font-medium hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
                >
                  [{mergeTagConflict.targetTag}]
                </button>
                <button
                  onClick={() => setMergeTagConflict(null)}
                  className="p-1.5 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
                  title="Cancel merge"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Floating merge bar — offer to fold freshly redacted text into an
              existing redaction group */}
          {!mergeTagConflict &&
            pendingMerge &&
            mergeTargets.length > 0 &&
            !isIdentifying &&
            selectedFieldIds.size === 0 && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30">
                <div className="bg-base-100 shadow-lg border border-base-300 rounded-lg px-4 py-2 flex items-center gap-3">
                  <span className="text-sm text-gray-700 dark:text-base-content/80 max-w-[14rem] truncate">
                    Redacted{" "}
                    <span className="font-medium">
                      &ldquo;{pendingMerge.text}&rdquo;
                    </span>
                  </span>
                  <div className="w-px h-5 bg-base-300" />
                  <select
                    className="h-7 px-2 text-sm border border-base-300 rounded bg-base-100 focus:outline-none focus:border-gray-400 dark:focus:border-base-content/30 transition-colors truncate max-w-[16rem]"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) mergePendingIntoGroup(e.target.value);
                    }}
                  >
                    <option value="" disabled>
                      Add to existing redaction...
                    </option>
                    {mergeTargets.map((g) => {
                      const label = g.text || "Manual redaction";
                      return (
                        <option key={g.key} value={g.key}>
                          {label.length > 40
                            ? `${label.slice(0, 40)}...`
                            : label}{" "}
                          ({g.category})
                        </option>
                      );
                    })}
                  </select>
                  <button
                    onClick={() => setPendingMerge(null)}
                    className="p-1.5 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
                    title="Keep as separate redaction"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

          {/* Floating bulk action bar */}
          <div
            className={cn(
              "absolute top-4 left-1/2 -translate-x-1/2 z-30 flex justify-center transition-all duration-200",
              !isIdentifying && !mergeTagConflict && selectedFieldIds.size > 0
                ? "opacity-100 translate-y-0 pointer-events-auto"
                : "opacity-0 -translate-y-2 pointer-events-none"
            )}
          >
            <div className="bg-base-100 shadow-lg border border-base-300 rounded-lg px-4 py-2 flex flex-col">
              {/* Row 1 — selection status + actions */}
              <div className="flex items-center gap-3">
                <span
                  className="text-sm font-medium text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 rounded inline-flex items-center gap-1"
                  title={
                    selectedGroupsAreaTotal > selectedFieldIds.size
                      ? `${selectedFieldIds.size} selected of ${selectedGroupsAreaTotal} areas in this group`
                      : `${selectedFieldIds.size} selected`
                  }
                >
                  <Squares2X2Icon className="h-3.5 w-3.5" />
                  <span>
                    {selectedFieldIds.size}
                    {selectedGroupsAreaTotal > selectedFieldIds.size && (
                      <span className="text-blue-400">
                        /{selectedGroupsAreaTotal}
                      </span>
                    )}
                  </span>
                </span>
                {selectedGroupKey && groupMergeTargets.length > 0 && (
                  <>
                    <div className="w-px h-5 bg-base-300" />
                    <select
                      className="h-7 flex-1 min-w-[10rem] px-2 text-sm border border-base-300 rounded bg-base-100 focus:outline-none focus:border-gray-400 dark:focus:border-base-content/30 transition-colors truncate"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          mergeGroups(selectedGroupKey, e.target.value);
                        }
                      }}
                    >
                      <option value="" disabled>
                        Merge into...
                      </option>
                      {groupMergeTargets.map((g) => {
                        const label = g.text || "Manual redaction";
                        return (
                          <option key={g.key} value={g.key}>
                            {label.length > 40
                              ? `${label.slice(0, 40)}...`
                              : label}{" "}
                            ({g.category})
                          </option>
                        );
                      })}
                    </select>
                  </>
                )}
                <div className="flex items-center gap-3 ml-auto">
                  <div className="w-px h-5 bg-base-300" />
                  <button
                    onClick={() => {
                      setAreas((prev) =>
                        prev.filter((a) => !selectedFieldIds.has(a.id))
                      );
                      marquee.clearSelection();
                      setPendingMerge(null);
                    }}
                    className="p-1.5 text-base-content/40 hover:text-red-500 transition-colors"
                    title="Delete selected redactions"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => marquee.clearSelection()}
                    className="p-1.5 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
                    title="Clear selection"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Row 2 — group properties */}
              {selectedGroupKey && selectedGroup && (
                <div className="flex items-center gap-4 mt-2 pt-2 border-t border-base-200 dark:border-base-300">
                  <label className="flex items-center gap-1.5 flex-1 min-w-0">
                    <span className="text-[11px] font-medium uppercase tracking-wide text-base-content/40">
                      Category
                    </span>
                    <select
                      className="h-7 flex-1 min-w-0 px-2 text-sm border border-base-300 rounded bg-base-100 focus:outline-none focus:border-gray-400 dark:focus:border-base-content/30 transition-colors truncate"
                      value={selectedGroup.category}
                      onChange={(e) =>
                        updateGroup(selectedGroupKey, {
                          category: e.target
                            .value as PdfRedactionArea["category"],
                        })
                      }
                    >
                      {CATEGORY_OPTIONS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5 flex-1 min-w-0">
                    <span className="text-[11px] font-medium uppercase tracking-wide text-base-content/40">
                      Footnote
                    </span>
                    <select
                      className="h-7 flex-1 min-w-0 px-2 text-sm border border-base-300 rounded bg-base-100 focus:outline-none focus:border-gray-400 dark:focus:border-base-content/30 transition-colors truncate"
                      value={selectedGroup.footnoteId || ""}
                      onChange={(e) => {
                        if (e.target.value === "__add__") {
                          addFootnoteForGroup(selectedGroupKey);
                          return;
                        }
                        updateGroup(selectedGroupKey, {
                          footnoteId: e.target.value || undefined,
                        });
                      }}
                    >
                      <option value="">None</option>
                      {footnotes.map((f) => {
                        const n = footnoteNumbers.get(f.id);
                        const label = footnoteLabel(f);
                        return (
                          <option key={f.id} value={f.id}>
                            {n ? `${n}. ` : ""}
                            {label.length > 40
                              ? `${label.slice(0, 40)}...`
                              : label}
                          </option>
                        );
                      })}
                      <option value="__add__">+ Add footnote...</option>
                    </select>
                  </label>
                  {/* Label size override (auto-size mode, tagged groups) */}
                  {!uniformTagSize && selectedGroup.tag && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-medium uppercase tracking-wide text-base-content/40">
                        Label
                      </span>
                      <button
                        onClick={() =>
                          updateGroup(selectedGroupKey, {
                            tagFontSize: Math.max(
                              TAG_FONT_MIN,
                              Math.round(
                                resolveTagFontPt(
                                  selectedGroup.areas[0],
                                  tagSizeOptions
                                )
                              ) - 1
                            ),
                          })
                        }
                        className="w-6 h-6 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-sm font-medium"
                        title="Smaller label"
                      >
                        -
                      </button>
                      <span className="text-xs text-gray-600 dark:text-base-content/70 w-8 text-center">
                        {Math.round(
                          resolveTagFontPt(
                            selectedGroup.areas[0],
                            tagSizeOptions
                          )
                        )}
                        pt
                      </span>
                      <button
                        onClick={() =>
                          updateGroup(selectedGroupKey, {
                            tagFontSize: Math.min(
                              TAG_FONT_MAX,
                              Math.round(
                                resolveTagFontPt(
                                  selectedGroup.areas[0],
                                  tagSizeOptions
                                )
                              ) + 1
                            ),
                          })
                        }
                        className="w-6 h-6 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-sm font-medium"
                        title="Larger label"
                      >
                        +
                      </button>
                      {selectedGroup.areas[0]?.tagFontSize !== undefined && (
                        <button
                          onClick={() =>
                            updateGroup(selectedGroupKey, {
                              tagFontSize: undefined,
                            })
                          }
                          className="text-xs text-base-content/40 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
                        >
                          Auto
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div
            ref={pdf.containerRef}
            tabIndex={-1}
            className={cn(
              "h-full overflow-auto bg-base-300 p-8 outline-none",
              pdf.panCursorClass ??
                (selectedTool === "redact" ? "cursor-text" : undefined)
            )}
            onMouseDown={handleContainerMouseDown}
            onMouseMove={handleContainerMouseMove}
            onMouseUp={handleContainerMouseUp}
            onMouseLeave={handleContainerMouseUp}
          >
            <div
              ref={pdf.canvasRef}
              className={cn(
                "relative mx-auto bg-white shadow-xl", /* paper — stays white in dark mode */
                selectedTool === "redact"
                  ? "cursor-text"
                  : selectedTool === "draw"
                    ? cn(
                        "select-none",
                        pdf.panCursorClass || "cursor-crosshair"
                      )
                    : cn("select-none", pdf.panCursorClass || "cursor-default")
              )}
              style={{
                width: pdf.pdfDimensions.width * scale || 612,
                height: pdf.pdfDimensions.height * scale || 792,
                transition: pdf.pageReady
                  ? "none"
                  : "width 0.15s ease, height 0.15s ease",
              }}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={() => {
                if (drawStart) {
                  setDrawStart(null);
                  setDrawCurrent(null);
                }
              }}
            >
              {!pdfFileUrl ? (
                <div className="flex items-center justify-center h-96">
                  <span className="loading loading-spinner loading-md text-primary"></span>
                </div>
              ) : (
                <Document
                  file={pdfFileUrl}
                  onLoadSuccess={pdf.onDocumentLoadSuccess}
                  loading={null}
                  error={
                    <div className="flex items-center justify-center h-96 text-error">
                      Failed to load PDF
                    </div>
                  }
                >
                  {pdf.numPages > 0 && (
                    <div
                      className="transition-opacity duration-200"
                      style={{ opacity: pdf.pageReady ? 1 : 0 }}
                    >
                      <Page
                        key={`page-${pdf.currentPage}-${scale}`}
                        pageNumber={pdf.currentPage}
                        scale={scale}
                        onLoadSuccess={pdf.onPageLoadSuccess}
                        onRenderSuccess={() => pdf.setPageReady(true)}
                        loading={<span />}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </div>
                  )}
                </Document>
              )}

              {!pdf.pageReady && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <span className="loading loading-spinner loading-md text-base-content/30" />
                </div>
              )}

              {/* Invisible selectable text layer (Redact Text tool) */}
              {pdf.pageReady &&
                selectedTool === "redact" &&
                selectionSpans.length > 0 && (
                  <div className="redaction-text-layer z-[5]">
                    {selectionSpans.map((s, i) => (
                      <span
                        key={i}
                        style={{
                          left: s.x * scale,
                          top: s.y * scale,
                          fontSize: `${s.fontSize}px`,
                          lineHeight: `${s.height * scale}px`,
                          height: s.height * scale,
                          transform: `scaleX(${s.scaleX})`,
                        }}
                      >
                        {s.str}
                      </span>
                    ))}
                  </div>
                )}

              {/* Search match highlights — same palette as the editor's
                  .search-result / .search-result-current styles */}
              {pdf.pageReady &&
                showSearch &&
                searchMatches.map((m, i) =>
                  m.page === pdf.currentPage
                    ? m.rects.map((r, j) => (
                        <div
                          key={`search-${i}-${j}`}
                          className="absolute pointer-events-none rounded-[3px]"
                          style={{
                            left: r.x * scale,
                            top: r.y * scale,
                            width: r.width * scale,
                            height: r.height * scale,
                            backgroundColor:
                              i === clampedMatchIdx ? "#f59e0b" : "#fef3c7",
                            boxShadow:
                              i === clampedMatchIdx
                                ? "0 0 0 2px #d97706"
                                : undefined,
                            opacity: 0.55,
                          }}
                        />
                      ))
                    : null
                )}

              {/* Draw Box preview — mirrors the final bar (solid black) so
                  the user sees exactly what will be redacted */}
              {pdf.pageReady && drawPreview && (
                <div
                  className="absolute pointer-events-none z-20 border-2 border-dashed border-blue-400 rounded-sm"
                  style={{
                    left: drawPreview.left,
                    top: drawPreview.top,
                    width: drawPreview.width,
                    height: drawPreview.height,
                    backgroundColor: "rgba(0, 0, 0, 0.75)",
                  }}
                />
              )}

              {/* Redaction overlays */}
              {pdf.pageReady &&
                currentPageAreas.map((area) => {
                  const isSelected = selectedFieldIds.has(area.id);
                  // Unselected sibling of a selected group member.
                  const isGroupMate =
                    !isSelected &&
                    selectedGroupKeys.has(area.groupId || area.id);

                  const boxClass = area.enabled
                    ? "border-black"
                    : "border-dashed border-gray-400";
                  // Inline styles so the black bar can never be lost to a
                  // stale Tailwind scan or a theme override — an unfilled
                  // redaction box is a data leak, not a styling nit.
                  const boxStyle: React.CSSProperties = area.enabled
                    ? { backgroundColor: "#000" }
                    : { backgroundColor: "rgba(254, 249, 195, 0.4)" };
                  const ringClass = isSelected
                    ? "ring-2 ring-blue-400 z-10"
                    : "";

                  // Labels wider than the bar shrink to fit (4pt floor)
                  // instead of disappearing — mirrors the export renderer.
                  let tagFontPt = resolveTagFontPt(area, tagSizeOptions);
                  const rawLabel =
                    area.enabled && showLabelsInView && area.tag
                      ? `[${area.tag}]`
                      : "";
                  if (rawLabel && measureCtx) {
                    measureCtx.font = `${tagFontPt}px Helvetica, Arial, sans-serif`;
                    const labelWidth = measureCtx.measureText(rawLabel).width;
                    const available = area.rect.width - 4;
                    if (labelWidth > available && labelWidth > 0) {
                      tagFontPt = Math.max(
                        4,
                        tagFontPt * (available / labelWidth)
                      );
                    }
                  }
                  // Labels taller than the bar shrink to fit (4pt floor)
                  // instead of disappearing — same policy as width overflow
                  // above, mirrored by the export renderer. Only sliver-thin
                  // bars (under ~3pt) drop the label entirely.
                  const maxFontByHeight =
                    area.rect.height + BAR_PADDING * 2 - 2;
                  if (rawLabel && tagFontPt > maxFontByHeight) {
                    tagFontPt = Math.max(4, maxFontByHeight);
                  }
                  const label =
                    rawLabel && maxFontByHeight >= 4 ? rawLabel : "";

                  // absolute inset-0 (not h-full flow layout) so the label
                  // renders identically in the plain div and the react-rnd
                  // wrapper — otherwise the text nudges when selection swaps
                  // the render path.
                  const content = label ? (
                    <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none">
                      <span
                        className="text-white whitespace-nowrap leading-none"
                        style={{ fontSize: `${tagFontPt * scale}px` }}
                      >
                        {label}
                      </span>
                    </div>
                  ) : null;

                  if (isSelected) {
                    const rndHandlers = fieldMovement.getRndHandlers(area.id);
                    return (
                      <Rnd
                        key={area.id}
                        position={fieldMovement.getFieldPosition(
                          area.id,
                          area.rect
                        )}
                        size={{
                          width: area.rect.width * scale,
                          height: area.rect.height * scale,
                        }}
                        enableResizing={selectedFieldIds.size === 1}
                        disableDragging={false}
                        resizeHandleStyles={{
                          top: { height: 4, top: -2 },
                          bottom: { height: 4, bottom: -2 },
                          left: { width: 4, left: -2 },
                          right: { width: 4, right: -2 },
                          topLeft: { width: 6, height: 6, top: -3, left: -3 },
                          topRight: { width: 6, height: 6, top: -3, right: -3 },
                          bottomLeft: {
                            width: 6,
                            height: 6,
                            bottom: -3,
                            left: -3,
                          },
                          bottomRight: {
                            width: 6,
                            height: 6,
                            bottom: -3,
                            right: -3,
                          },
                        }}
                        onDragStart={() => {
                          wasDraggedRef.current = false;
                          setIsDraggingField(true);
                          rndHandlers.onDragStart();
                        }}
                        onDrag={(e: any, d: { x: number; y: number }) => {
                          wasDraggedRef.current = true;
                          rndHandlers.onDrag(e, d);
                        }}
                        onDragStop={(e: any, d: { x: number; y: number }) => {
                          setIsDraggingField(false);
                          rndHandlers.onDragStop(e, d);
                        }}
                        onResizeStart={() => {
                          wasDraggedRef.current = true;
                        }}
                        onResize={rndHandlers.onResize}
                        onResizeStop={rndHandlers.onResizeStop}
                        bounds="parent"
                        style={{
                          cursor: isDraggingField ? "move" : "pointer",
                          ...boxStyle,
                        }}
                        className={cn(
                          "border rounded-sm transition-colors",
                          boxClass,
                          ringClass
                        )}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          if (!wasDraggedRef.current) {
                            // Second click of a double-click (the first click
                            // selected the bar and swapped it into this Rnd
                            // wrapper) opens the tag editor.
                            if (e.detail >= 2) {
                              openTagModal(area);
                            } else {
                              marquee.handleFieldClick(area.id, e.shiftKey);
                            }
                          }
                          wasDraggedRef.current = false;
                        }}
                      >
                        {content}
                      </Rnd>
                    );
                  }

                  const isTempDragging = tempDrag?.areaId === area.id;

                  return (
                    <div
                      key={area.id}
                      className={cn(
                        "absolute border rounded-sm transition-colors hover:ring-2 hover:ring-blue-300 select-none",
                        boxClass,
                        // Secondary highlight: this bar belongs to the same
                        // group as the selected one. Deliberately a lighter,
                        // different hue than the blue selection ring.
                        isGroupMate &&
                          "ring-2 ring-violet-300/70 ring-offset-1",
                        // In Redact Text mode bars sit above the text layer
                        // and stay grabbable: mousedown drags them for the
                        // duration of the hold, then the tool reverts to
                        // text selection.
                        selectedTool === "redact" && "z-10",
                        isTempDragging
                          ? "cursor-grabbing"
                          : selectedTool === "draw"
                            ? "cursor-crosshair"
                            : "cursor-pointer"
                      )}
                      style={{
                        // Positioned via transform (not left/top) to match
                        // react-rnd's mechanics exactly — with fractional
                        // coordinates the two strategies rasterize a hair
                        // apart, which made bars nudge when selection
                        // swapped the render path.
                        left: 0,
                        top: 0,
                        transform: `translate(${
                          area.rect.x * scale +
                          (isTempDragging ? tempDrag.dx : 0)
                        }px, ${
                          area.rect.y * scale +
                          (isTempDragging ? tempDrag.dy : 0)
                        }px)`,
                        width: area.rect.width * scale,
                        height: area.rect.height * scale,
                        ...boxStyle,
                        ...(isTempDragging ? { transition: "none" } : {}),
                      }}
                      title={
                        area.enabled
                          ? area.tag
                            ? `Redacted: ${area.tag}`
                            : "Redacted"
                          : "Not redacted (disabled)"
                      }
                      onMouseDown={(e) => handleBarMouseDown(e, area.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (e.detail >= 2) {
                          openTagModal(area);
                          return;
                        }
                        if (selectedTool !== "redact") {
                          marquee.handleFieldClick(area.id, e.shiftKey);
                        }
                      }}
                    >
                      {content}
                    </div>
                  );
                })}

              {/* Alignment snap guides (drawn while dragging a bar) */}
              {pdf.pageReady && (
                <SnapGuideLines guides={snap.guides} scale={scale} />
              )}

              {/* Marquee selection rectangle */}
              {pdf.pageReady && marquee.marqueePreview && (
                <div
                  className="absolute border border-blue-500 bg-blue-500/10 pointer-events-none z-20"
                  style={marquee.marqueePreview}
                />
              )}
            </div>
          </div>

          <PdfFloatingNav
            currentPage={pdf.currentPage}
            numPages={pdf.numPages}
            scale={scale}
            onPrevPage={pdf.prevPage}
            onNextPage={pdf.nextPage}
            onZoomIn={pdf.zoomIn}
            onZoomOut={pdf.zoomOut}
            onZoomFit={pdf.zoomFit}
            minScale={0.25}
            maxScale={3}
          />
        </div>

        {/* Sidebar */}
        <div className="flex-shrink-0 flex flex-row">
          <button
            onClick={() => setSidebarCollapsed((v) => !v)}
            className="w-6 flex-shrink-0 border-l border-base-300 bg-base-200 hover:bg-blue-50 dark:hover:bg-blue-900/40 flex items-center justify-center cursor-pointer transition-colors group"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <span className="text-[10px] font-medium text-base-content/40 group-hover:text-blue-600 dark:group-hover:text-blue-300 tracking-wider uppercase [writing-mode:vertical-lr] rotate-180 select-none flex items-center gap-1">
              {sidebarCollapsed ? (
                <ChevronRightIcon className="h-3 w-3" />
              ) : (
                <ChevronLeftIcon className="h-3 w-3" />
              )}
              {sidebarCollapsed ? "Expand" : "Collapse"}
            </span>
          </button>
          {!sidebarCollapsed && (
            <div
              style={{ width: sidebarPanel.width }}
              className="relative shrink-0 bg-base-100 border-l border-base-300 flex flex-col overflow-hidden"
            >
              <ResizeHandle edge="left" active={sidebarPanel.isResizing} {...sidebarPanel.handleProps} />
              {/* Tabs */}
              <div className="flex border-b border-base-300">
                <button
                  onClick={() => setSidebarTab("redactions")}
                  className={cn(
                    "flex-1 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
                    sidebarTab === "redactions"
                      ? "border-blue-500 text-blue-600 dark:text-blue-300"
                      : "border-transparent text-base-content/60 hover:text-gray-700 dark:hover:text-base-content/80"
                  )}
                >
                  Redactions ({groups.length})
                </button>
                <button
                  onClick={() => setSidebarTab("footnotes")}
                  className={cn(
                    "flex-1 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
                    sidebarTab === "footnotes"
                      ? "border-blue-500 text-blue-600 dark:text-blue-300"
                      : "border-transparent text-base-content/60 hover:text-gray-700 dark:hover:text-base-content/80"
                  )}
                >
                  Footnotes ({footnoteNumbers.size})
                </button>
              </div>

              {sidebarTab === "redactions" && (
              <div className="flex-1 overflow-auto">
                {isIdentifying && groups.length === 0 ? (
                  <div className="p-4 space-y-3">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="h-16 bg-base-200 rounded-lg animate-pulse"
                      />
                    ))}
                  </div>
                ) : groups.length === 0 ? (
                  <div className="p-4 text-center text-base-content/40 text-sm">
                    <p>No redactions yet.</p>
                    <p className="mt-2">
                      Use the <span className="font-medium">Redact Text</span>{" "}
                      tool to select text on the PDF, or{" "}
                      <span className="font-medium">Re-identify</span> to run
                      AI detection.
                    </p>
                  </div>
                ) : (
                  <div className="p-3 space-y-2">
                    {groups.map((group) => {
                      const firstPage = group.areas[0]?.page;
                      const isGroupSelected = group.areas.some((a) =>
                        selectedFieldIds.has(a.id)
                      );
                      return (
                        <div
                          key={group.key}
                          ref={(el) => {
                            if (el) {
                              groupRefs.current.set(group.key, el);
                            } else {
                              groupRefs.current.delete(group.key);
                            }
                          }}
                          className={cn(
                            "p-2.5 rounded-lg border cursor-pointer transition-colors",
                            isGroupSelected
                              ? "border-blue-400 bg-blue-50 dark:bg-blue-900/40"
                              : "border-base-300 hover:border-gray-300 dark:hover:border-base-content/30 bg-base-200",
                            !group.enabled && "opacity-60"
                          )}
                          onClick={() => jumpToGroup(group)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-base-content break-words min-w-0">
                              {group.text || "Manual redaction"}
                            </p>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Tooltip
                                overlay={
                                  group.enabled ? "Unredact" : "Redact again"
                                }
                                placement="left"
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateGroup(group.key, {
                                      enabled: !group.enabled,
                                    });
                                  }}
                                  className={cn(
                                    "p-1 rounded transition-colors",
                                    group.enabled
                                      ? "text-base-content/60 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40"
                                      : "text-yellow-600 dark:text-amber-400 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40"
                                  )}
                                >
                                  {group.enabled ? (
                                    <EyeIcon className="h-4 w-4" />
                                  ) : (
                                    <EyeSlashIcon className="h-4 w-4" />
                                  )}
                                </button>
                              </Tooltip>
                              <Tooltip overlay="Remove from list" placement="left">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteGroup(group.key);
                                  }}
                                  className="p-1 rounded text-base-content/40 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </Tooltip>
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <span
                              className={cn(
                                "px-1.5 py-0.5 rounded text-[11px] font-medium",
                                CATEGORY_COLORS[group.category] ||
                                  CATEGORY_COLORS.Other
                              )}
                            >
                              {group.category}
                            </span>
                            {group.source === "manual" && (
                              <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-base-300 text-slate-700 dark:text-base-content/80">
                                Manual
                              </span>
                            )}
                            {group.footnoteId &&
                              footnoteNumbers.get(group.footnoteId) && (
                                <span
                                  className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-800 text-white"
                                  title={footnoteLabel(
                                    footnotes.find(
                                      (f) => f.id === group.footnoteId
                                    ) || { id: "" }
                                  )}
                                >
                                  fn {footnoteNumbers.get(group.footnoteId)}
                                </span>
                              )}
                            {firstPage !== undefined && (
                              <span className="text-[11px] text-base-content/40">
                                p. {firstPage}
                                {group.areas.length > 1 &&
                                  ` · ${group.areas.length} areas`}
                              </span>
                            )}
                          </div>

                          {/* Tag (label shown inside the black bar) */}
                          <div
                            className="mt-1.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {editingTagKey === group.key ? (
                              <input
                                type="text"
                                autoFocus
                                className="w-full h-6 px-2 text-xs border border-blue-300 dark:border-blue-800 rounded bg-base-100 focus:outline-none"
                                placeholder='Bar label, e.g. "victim"'
                                value={tagDraft}
                                onChange={(e) => setTagDraft(e.target.value)}
                                onBlur={() => saveTagEdit(group.key)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveTagEdit(group.key);
                                  if (e.key === "Escape")
                                    setEditingTagKey(null);
                                }}
                              />
                            ) : (
                              <button
                                onClick={() => startTagEdit(group)}
                                className="flex items-center gap-1 text-[11px] text-base-content/40 hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
                              >
                                <PencilIcon className="h-3 w-3" />
                                {group.tag ? (
                                  <span>
                                    Tag:{" "}
                                    <span className="font-medium">
                                      [{group.tag}]
                                    </span>
                                  </span>
                                ) : (
                                  <span>Add tag</span>
                                )}
                              </button>
                            )}
                          </div>

                          {group.reason && (
                            <p className="text-[11px] text-base-content/40 mt-1">
                              {group.reason}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              )}

              {/* Footnotes tab */}
              {sidebarTab === "footnotes" && (
                <>
                  <div className="flex-1 overflow-auto">
                    {footnotes.length === 0 ? (
                      <div className="p-4 text-center text-base-content/40 text-sm">
                        <p>No footnotes yet.</p>
                        <p className="mt-2">
                          Footnotes are created automatically when the AI
                          cites a statute, or add one below. Assign a
                          redaction to a footnote from its floating bar.
                        </p>
                      </div>
                    ) : (
                      <div className="p-3 space-y-2">
                        {footnotes.map((f) => {
                          const n = footnoteNumbers.get(f.id);
                          const usage = footnoteUsage.get(f.id) || 0;
                          return (
                            <div
                              key={f.id}
                              className="p-2.5 rounded-lg border border-base-300 bg-base-200"
                            >
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <span
                                  className={cn(
                                    "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold",
                                    n
                                      ? "bg-gray-800 text-white"
                                      : "bg-base-300 text-base-content/40"
                                  )}
                                  title={
                                    n
                                      ? `Footnote ${n}`
                                      : "Empty footnotes are not exported"
                                  }
                                >
                                  {n ?? "–"}
                                </span>
                                <span className="text-[11px] text-base-content/40 flex-1">
                                  {usage} redaction{usage === 1 ? "" : "s"}{" "}
                                  assigned
                                </span>
                                <Tooltip
                                  overlay="Look up this footnote on the web"
                                  placement="left"
                                >
                                  <button
                                    disabled={
                                      !f.statute?.trim() &&
                                      !f.justification?.trim()
                                    }
                                    onClick={() => {
                                      const query = (
                                        f.statute?.trim() ||
                                        f.justification?.trim() ||
                                        ""
                                      ).trim();
                                      if (!query) return;
                                      window.open(
                                        `https://www.google.com/search?q=${encodeURIComponent(
                                          query
                                        )}`,
                                        "_blank",
                                        "noopener,noreferrer"
                                      );
                                    }}
                                    className="p-1 rounded text-base-content/40 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-base-content/40"
                                  >
                                    <MagnifyingGlassIcon className="h-4 w-4" />
                                  </button>
                                </Tooltip>
                                <Tooltip
                                  overlay="Delete footnote (its redactions are unassigned)"
                                  placement="left"
                                >
                                  <button
                                    onClick={() => deleteFootnote(f.id)}
                                    className="p-1 rounded text-base-content/40 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </Tooltip>
                              </div>
                              <input
                                type="text"
                                ref={(el) => {
                                  // Focus the statute box of a footnote just
                                  // created from the action bar's dropdown.
                                  if (
                                    el &&
                                    pendingFocusFootnoteRef.current === f.id
                                  ) {
                                    pendingFocusFootnoteRef.current = null;
                                    el.focus();
                                  }
                                }}
                                className="w-full h-7 px-2 mb-1.5 text-sm border border-base-300 rounded bg-base-100 focus:outline-none focus:border-blue-400 transition-colors"
                                placeholder='Statute, e.g. "16 M.R.S. §804(3)"'
                                value={f.statute || ""}
                                onChange={(e) =>
                                  updateFootnote(f.id, {
                                    statute: e.target.value,
                                  })
                                }
                              />
                              <textarea
                                className="w-full px-2 py-1.5 text-sm border border-base-300 rounded bg-base-100 focus:outline-none focus:border-blue-400 transition-colors resize-y"
                                rows={2}
                                placeholder='Justification, e.g. "Constitutes an invasion of privacy"'
                                value={f.justification || ""}
                                onChange={(e) =>
                                  updateFootnote(f.id, {
                                    justification: e.target.value,
                                  })
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="px-3 py-2.5 border-t border-base-300">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={addFootnote}
                      className="w-full"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Add footnote
                    </Button>
                  </div>
                </>
              )}

            </div>
          )}
        </div>
      </div>

      {/* Export acknowledgment dialog — mirrors the case export workflow */}
      <dialog ref={exportDialogRef} className="modal">
        <div className="modal-box prose w-11/12 md:w-5/12 p-8">
          <h2>Review and acknowledge</h2>
          <p>
            I affirm that I have reviewed this document and the redactions
            applied to it for accuracy and completeness, confirming that all
            sensitive and personally-identifying information intended for
            removal is fully covered. I acknowledge the potential legal
            implications of releasing this redacted document and, if required,
            am prepared to testify to its accuracy.
          </p>

          {enabledCount === 0 && (
            <div className="not-prose mb-4 rounded-md border border-amber-300 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-400">
              This document has no active redactions — the exported PDF will
              contain no black bars.
            </div>
          )}

          <div className="not-prose mb-4 grid gap-1.5 rounded-lg border border-base-300 bg-base-200 p-2">
            {/* Tag labels */}
            {(() => {
              const disabled = taggedGroupCount === 0;
              return (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    const next = !exportShowTags;
                    setExportShowTags(next);
                    localStorage.setItem(
                      "redactionDefaults:exportTags",
                      String(next)
                    );
                  }}
                  className={cn(
                    "w-full flex items-start gap-2 rounded-md border p-2 text-left transition",
                    disabled
                      ? "border-base-300 bg-base-200 opacity-50 pointer-events-none"
                      : exportShowTags
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40"
                        : "border-base-300 bg-base-100 hover:border-blue-300 dark:hover:border-blue-800"
                  )}
                >
                  <span
                    className={cn(
                      "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
                      exportShowTags && !disabled
                        ? "border-blue-500 bg-blue-500 text-white"
                        : "border-gray-300 dark:border-base-300 bg-base-100 text-transparent"
                    )}
                  >
                    <CheckIcon className="size-3" strokeWidth={3} />
                  </span>
                  <span className="block">
                    <span className="block text-xs font-semibold text-base-content">
                      Show tags on bars
                    </span>
                    <span className="block text-[11px] text-base-content/60 leading-snug">
                      {disabled
                        ? "No redactions have tags yet."
                        : `Prints a label like [victim] inside the bar so readers know what was removed (${taggedGroupCount} tagged).`}
                    </span>
                  </span>
                </button>
              );
            })()}
            {/* Footnotes */}
            {(() => {
              const disabled = !hasFootnotes;
              return (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    const next = !exportShowFootnotes;
                    setExportShowFootnotes(next);
                    localStorage.setItem(
                      "redactionDefaults:exportFootnotes",
                      String(next)
                    );
                  }}
                  className={cn(
                    "w-full flex items-start gap-2 rounded-md border p-2 text-left transition",
                    disabled
                      ? "border-base-300 bg-base-200 opacity-50 pointer-events-none"
                      : exportShowFootnotes
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40"
                        : "border-base-300 bg-base-100 hover:border-blue-300 dark:hover:border-blue-800"
                  )}
                >
                  <span
                    className={cn(
                      "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
                      exportShowFootnotes && !disabled
                        ? "border-blue-500 bg-blue-500 text-white"
                        : "border-gray-300 dark:border-base-300 bg-base-100 text-transparent"
                    )}
                  >
                    <CheckIcon className="size-3" strokeWidth={3} />
                  </span>
                  <span className="block">
                    <span className="block text-xs font-semibold text-base-content">
                      Legal justification footnotes
                    </span>
                    <span className="block text-[11px] text-base-content/60 leading-snug">
                      {disabled
                        ? "No footnotes yet — add them from the Footnotes panel."
                        : "Numbers each redaction and appends the statutory footnotes at the end of the document."}
                    </span>
                  </span>
                </button>
              );
            })()}
          </div>

          <form method="dialog">
            <button className="btn absolute top-4 right-4 z-10 border-0 shadow-none">
              <XMarkIcon className="size-6" />
            </button>
          </form>
          <Button
            onClick={handleExport}
            className="btn-primary w-full"
            variant="primary"
            disabled={isExporting}
          >
            {isExporting ? "Exporting..." : "Agree and export redacted PDF"}
          </Button>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>

      {/* Re-identify modal — lets the officer narrow the AI's scope before
          re-running (e.g. "only redact minors"). Persisted on the document. */}
      <dialog ref={reIdentifyDialogRef} className="modal">
        <div className="modal-box max-w-lg">
          <h3 className="font-bold text-lg">Re-identify redactions</h3>
          <p className="text-sm text-base-content/70 mt-1">
            AI-identified redactions will be replaced. Manually drawn
            redactions are kept.
          </p>

          <div className="mt-4">
            <TextArea
              label="Redaction Instructions (optional)"
              value={instructionsDraft}
              onChange={(e) => setInstructionsDraft(e.target.value)}
              placeholder="e.g., Only redact minors. Leave suspect and officer names visible."
              rows={3}
              maxLength={1000}
              labelSize="sm"
              labelClassName="!min-h-7 mb-1"
            />
            <p className="text-xs text-base-content/60 -mt-2">
              Leave blank to scan for all standard PII.
            </p>
          </div>

          <div className="modal-action">
            <form method="dialog">
              <Button variant="secondary">Cancel</Button>
            </form>
            <Button
              variant="primary"
              onClick={handleConfirmReIdentify}
              disabled={isIdentifying}
            >
              Run
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>

      {/* Tag edit modal (opened by double-clicking a bar on the canvas) */}
      {tagModal && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-sm">
            <h3 className="font-bold text-lg">Edit Tag</h3>
            <p className="py-2 text-sm text-base-content/60">
              The label printed inside the redaction bar, e.g. [victim].
            </p>
            <input
              type="text"
              autoFocus
              className="w-full h-10 px-3 text-sm border border-gray-300 dark:border-base-300 rounded-lg bg-base-100 focus:outline-none focus:border-blue-400 transition-colors"
              placeholder='Bar label, e.g. "victim"'
              value={tagModalDraft}
              onChange={(e) => setTagModalDraft(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") saveTagModal();
                if (e.key === "Escape") setTagModal(null);
              }}
            />
            {tagModal.areaCount > 1 && (
              <p className="mt-3 text-sm text-amber-800 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg px-3 py-2">
                This redaction spans{" "}
                <span className="font-medium">
                  {tagModal.areaCount} areas
                </span>{" "}
                — the label will be updated on all of them.
              </p>
            )}
            <div className="modal-action">
              <Button variant="secondary" onClick={() => setTagModal(null)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveTagModal}>
                {tagModal.areaCount > 1
                  ? `Update ${tagModal.areaCount} areas`
                  : "Save"}
              </Button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={() => setTagModal(null)}>close</button>
          </form>
        </dialog>
      )}
    </div>
  );
};

export default RedactionEditor;
